import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PACKAGES } from "../data/content";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

export default function Packages() {
  return (
    <div data-testid="page-packages">
      <Seo
        title="Packages — Right-Sized Engagements for Every Stage of Growth"
        description="Business Foundation, Digital Presence, Marketing Management, Operations Improvement, and Full-Service Growth Partnership packages tailored to Dallas–Fort Worth businesses."
        path="/packages"
      />
      <section className="bg-white pt-20 pb-10">
        <div className="container-wide max-w-4xl">
          <div className="overline mb-6">Packages</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="packages-page-headline">
            Recurring engagements built around your growth stage.
          </h1>
          <p className="mt-6 text-lg text-[#4B5563] leading-relaxed">
            The Mogul Group Advisors operates on recurring monthly engagements — strategic partnerships, not one-off tasks. Choose the level that matches where your business is today. Custom scopes available on request.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-grid">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <div className={`p-8 border h-full flex flex-col ${p.highlight ? "bg-[#0A0A0A] text-white border-white/10" : "bg-white border-gray-100"}`} data-testid={`package-card-${p.slug}`}>
                  {p.highlight && <div className="overline mb-3" style={{ color: "#C19B54" }}>Most requested</div>}
                  <h3 className="font-serif text-2xl leading-tight">{p.name}</h3>
                  <p className={`mt-3 text-sm ${p.highlight ? "text-[#D1D5DB]" : "text-[#4B5563]"} leading-relaxed`}>{p.for}</p>
                  <ul className="mt-6 space-y-3 text-sm flex-1">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-2 items-start"><Check className={`w-4 h-4 mt-1 shrink-0 ${p.highlight ? "text-gold" : "text-gold"}`} strokeWidth={2} />{it}</li>
                    ))}
                  </ul>
                  <div className="mt-8 pt-6 border-t border-gray-200/40 flex flex-col gap-3">
                    <div className={`text-xs ${p.highlight ? "text-[#A1A1AA]" : "text-[#6B7280]"}`}>Starting at</div>
                    <div className="font-serif text-xl">
                      {p.startingAt ? p.startingAt : <span className={`${p.highlight ? "text-[#A1A1AA]" : "text-[#9CA3AF]"} text-sm italic`}>Custom quote</span>}
                    </div>
                    <div className={`text-[11px] uppercase tracking-widest ${p.highlight ? "text-gold" : "text-gold"} flex items-center gap-1.5`}>
                      <span>◆</span> Payment plans available with deposit
                    </div>
                    <Link to="/contact" className={`btn ${p.highlight ? "btn-primary" : "btn-secondary"} mt-2`} data-testid={`package-cta-${p.slug}`}>
                      Request a Quote <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* PAYMENT PLANS CALLOUT */}
          <div className="mt-14 bg-[#0A0A0A] text-white grain relative overflow-hidden" data-testid="payment-plans-callout">
            <div className="relative z-10 p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="overline mb-4" style={{ color: "#C19B54" }}>Flexible Terms</div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight" data-testid="payment-plans-headline">
                  Payment plans available — start with a deposit.
                </h2>
                <p className="mt-5 text-[#D1D5DB] leading-relaxed max-w-2xl">
                  We understand cash flow matters. Most engagements can be structured with a deposit and monthly payments — so investing in your business doesn't mean straining your operations. Custom terms are discussed during your consultation and finalized in your written agreement.
                </p>
                <ul className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
                  <li className="flex gap-2 items-start text-[#E5E7EB]"><span className="text-gold">◆</span> Deposit-based start</li>
                  <li className="flex gap-2 items-start text-[#E5E7EB]"><span className="text-gold">◆</span> Monthly payment options</li>
                  <li className="flex gap-2 items-start text-[#E5E7EB]"><span className="text-gold">◆</span> Custom scope, custom terms</li>
                </ul>
              </div>
              <div className="md:col-span-4">
                <Link to="/contact" className="btn btn-primary w-full justify-center" data-testid="payment-plans-cta">
                  Discuss Payment Options <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <p className="mt-4 text-xs text-[#A1A1AA] text-center">All terms subject to a signed engagement agreement.</p>
              </div>
            </div>
          </div>

          <div className="mt-14 bg-white p-8 md:p-12 border border-gray-100" data-testid="package-compare">
            <h2 className="font-serif text-2xl md:text-3xl">Compare packages at a glance</h2>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-3 pr-4 font-medium text-[#6B7280]">Package</th>
                    <th className="py-3 px-3 font-medium text-[#6B7280]">Best for</th>
                    <th className="py-3 px-3 font-medium text-[#6B7280]">Deliverables</th>
                    <th className="py-3 pl-3 font-medium text-[#6B7280]">Pricing</th>
                  </tr>
                </thead>
                <tbody>
                  {PACKAGES.map((p) => (
                    <tr key={p.slug} className="border-b border-gray-100 align-top">
                      <td className="py-4 pr-4 font-medium">{p.name}</td>
                      <td className="py-4 px-3 text-[#4B5563]">{p.for}</td>
                      <td className="py-4 px-3 text-[#4B5563]">{p.items.length} deliverables</td>
                      <td className="py-4 pl-3 text-[#4B5563]">{p.startingAt || "Custom quote"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-xs text-[#6B7280]">Every engagement is scoped in a written agreement. Projects typically start with a deposit and can be structured with monthly payments — engagement length varies based on scope. Talk to Alex about the right fit for your business.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
